import React, { useState } from "react";
import "../../Styles/YuvagalamForm.css";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { yuvagalamAPI } from "../../api/index";

const MANDAL_NAMES = ["Kandukur", "Lingasamudram", "Gudluru", "Ulavapadu", "Voletivaripalem"];

const STATUS_OPTIONS = ["pending", "approved", "rejected", "disbursed"];
const STATUS_LABEL = {
  pending: "Pending",
  approved: "Approved",
  rejected: "Rejected",
  disbursed: "Disbursed",
};

// Age limits for Yuvagalam (change here if your scheme rules differ)
const MIN_AGE = 18;
const MAX_AGE = 40;

const INITIAL_FORM = {
  name: "",
  relation: "",
  voterId: "",
  age: "",
  qualification: "",
  aadhaar: "",
  mobile: "",
  bankAccount: "",
  ifsc: "",
  amount: "",
  village: "",
  mandal: "",
  date: "",
  status: "",
  photoUrl: "",
  documentUrl: "",
  remarks: "",
};

// Order used to focus the first invalid field on submit (same as the form layout)
const FIELD_ORDER = [
  "name",
  "relation",
  "voterId",
  "age",
  "qualification",
  "aadhaar",
  "mobile",
  "bankAccount",
  "ifsc",
  "amount",
  "village",
  "mandal",
  "date",
  "status",
  "photoUrl",
  "documentUrl",
  "remarks",
];

// These fields show a format error immediately while typing
const LIVE_VALIDATE_FIELDS = ["voterId", "ifsc", "village", "amount"];

// Fields that accept digits only (other characters are blocked automatically)
const DIGITS_ONLY_FIELDS = ["age", "aadhaar", "mobile", "bankAccount"];

const NAME_REGEX = /^[A-Za-z\s.']+$/;
const RELATION_REGEX = /^[A-Za-z\s./'\-]+$/;
const QUALIFICATION_REGEX = /^[A-Za-z0-9\s.,\-&/()+]+$/;
const VILLAGE_REGEX = /^[A-Za-z\s]+$/;
const VOTER_ID_REGEX = /^[A-Z]{3}[0-9]{7}$/;
const AADHAAR_REGEX = /^[2-9][0-9]{11}$/;
const MOBILE_REGEX = /^[6-9][0-9]{9}$/;
const BANK_ACCOUNT_REGEX = /^[0-9]{9,18}$/;
const IFSC_REGEX = /^[A-Z]{4}0[A-Z0-9]{6}$/;
const AMOUNT_REGEX = /^\d+(\.\d{1,2})?$/;

const extractErrorMessage = (err) => {
  if (!err.response) {
    return `Network error — could not reach the server (${err.message}).`;
  }
  const status = err.response.status;
  const detail = err.response.data?.detail ?? err.response.data?.message;

  if (status === 404) {
    return typeof detail === "string"
      ? `[404] ${detail} — double check the spelling of the Village/Mandal name.`
      : `[404] Village or Mandal not recognized. Check spelling and try again.`;
  }
  if (typeof detail === "string") return `[${status}] ${detail}`;
  if (Array.isArray(detail)) {
    const parts = detail.slice(0, 3).map((d) => {
      if (d.row !== undefined) return `Row ${d.row}: ${d.reason}`;
      if (d.loc) return `${d.loc.join(".")}: ${d.msg}`;
      return d.msg || d.reason || JSON.stringify(d);
    });
    return `[${status}] ${parts.join(" | ")}`;
  }
  return `[${status}] Request failed — see console for full response body`;
};

const isLikelyUrl = (url) => {
  if (!url.trim()) return true;
  try {
    const u = new URL(url);
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
};

// ---------- Field validation rules ----------
const validateField = (name, value) => {
  const v = typeof value === "string" ? value.trim() : value;

  switch (name) {
    case "name":
      if (!v) return "Beneficiary name is required";
      if (v.length < 3) return "Name must be at least 3 characters";
      if (v.length > 100) return "Name must not exceed 100 characters";
      if (!NAME_REGEX.test(v)) return "Name can contain only alphabets, spaces, '.' and '''";
      return "";

    case "relation":
      if (!v) return "Relation is required (e.g. S/O Ramesh)";
      if (v.length < 2) return "Relation must be at least 2 characters";
      if (v.length > 100) return "Relation must not exceed 100 characters";
      if (!RELATION_REGEX.test(v))
        return "Relation can contain only alphabets, spaces and . / ' -";
      return "";

    case "voterId":
      if (!v) return "Voter Id is required";
      if (!VOTER_ID_REGEX.test(v))
        return "Enter a valid Voter Id (3 letters + 7 digits, e.g. ABC1234567)";
      return "";

    case "age": {
      if (!v) return "Age is required";
      if (!/^\d+$/.test(v)) return "Age must be a whole number";
      const n = Number(v);
      if (n < MIN_AGE) return `Age must be at least ${MIN_AGE}`;
      if (n > MAX_AGE) return `Age must not exceed ${MAX_AGE}`;
      return "";
    }

    case "qualification":
      if (!v) return "Qualification is required (e.g. B.Tech, Degree, ITI)";
      if (v.length < 2) return "Qualification must be at least 2 characters";
      if (v.length > 100) return "Qualification must not exceed 100 characters";
      if (!QUALIFICATION_REGEX.test(v))
        return "Qualification can contain only letters, numbers, spaces and . , - & / ( ) +";
      return "";

    case "aadhaar":
      if (!v) return "Aadhaar number is required";
      if (!/^\d+$/.test(v)) return "Aadhaar number must contain digits only";
      if (v.length !== 12) return "Aadhaar number must be exactly 12 digits";
      if (!AADHAAR_REGEX.test(v)) return "Aadhaar number cannot start with 0 or 1";
      return "";

    case "mobile":
      if (!v) return "Mobile number is required";
      if (!/^\d+$/.test(v)) return "Mobile number must contain digits only";
      if (v.length !== 10) return "Mobile number must be exactly 10 digits";
      if (!MOBILE_REGEX.test(v)) return "Mobile number must start with 6, 7, 8 or 9";
      return "";

    case "bankAccount":
      if (!v) return "Bank account number is required";
      if (!/^\d+$/.test(v)) return "Bank account number must contain digits only";
      if (!BANK_ACCOUNT_REGEX.test(v)) return "Bank account number must be 9 to 18 digits";
      return "";

    case "ifsc":
      if (!v) return "IFSC code is required";
      if (!IFSC_REGEX.test(v))
        return "Enter a valid IFSC code (4 letters + 0 + 6 letters/digits, e.g. SBIN0001234)";
      return "";

    case "amount":
      if (!v) return "Monthly allowance is required";
      if (!AMOUNT_REGEX.test(v)) return "Enter numbers only (e.g. 3000 or 3000.50)";
      if (Number(v) <= 0) return "Monthly allowance must be greater than 0";
      if (Number(v) > 1000000) return "Monthly allowance is too large";
      return "";

    case "village":
      if (!v) return "Village is required";
      if (!VILLAGE_REGEX.test(v)) return "Village name can contain only alphabets (A-Z)";
      if (v.length < 2) return "Village name must be at least 2 characters";
      if (v.length > 100) return "Village name must not exceed 100 characters";
      return "";

    case "mandal":
      if (!v) return "Please select a Mandal";
      if (!MANDAL_NAMES.includes(v)) return "Please select a valid Mandal";
      return "";

    case "date": {
      if (!v) return "Date is required";
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return "Enter a valid date";
      if (d.getFullYear() < 1990) return "Date cannot be before 1990";
      if (d.getFullYear() > 2100) return "Date cannot be after 2100";
      return "";
    }

    case "status":
      if (!v) return "Please select a Status";
      if (!STATUS_OPTIONS.includes(v)) return "Please select a valid Status";
      return "";

    case "photoUrl":
      if (!v) return ""; // optional
      if (!isLikelyUrl(v)) return "Enter a valid link starting with http:// or https://";
      return "";

    case "documentUrl":
      if (!v) return ""; // optional
      if (!isLikelyUrl(v)) return "Enter a valid link starting with http:// or https://";
      return "";

    case "remarks":
      if (!v) return ""; // optional
      if (v.length < 5) return "Remarks must be at least 5 characters";
      if (v.length > 500) return "Remarks must not exceed 500 characters";
      return "";

    default:
      return "";
  }
};

const validateAll = (data) => {
  const errors = {};
  FIELD_ORDER.forEach((name) => {
    const msg = validateField(name, data[name]);
    if (msg) errors[name] = msg;
  });
  return errors;
};

const YuvagalamForm = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const showError = (name) => touched[name] && errors[name];
  const inputClass = (name) => (showError(name) ? "input-error" : "");

  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;

    // Auto-format specific fields
    if (name === "voterId" || name === "ifsc") {
      value = value.toUpperCase().replace(/\s/g, "");
    }
    if (DIGITS_ONLY_FIELDS.includes(name)) {
      value = value.replace(/\D/g, "");
    }

    setFormData((prev) => ({ ...prev, [name]: value }));

    const isLive = LIVE_VALIDATE_FIELDS.includes(name);
    if (touched[name] || isLive) {
      if (isLive && !touched[name]) {
        setTouched((prev) => ({ ...prev, [name]: true }));
      }
      setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    setErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
  };

  const handleReset = () => {
    setFormData(INITIAL_FORM);
    setErrors({});
    setTouched({});
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate every field and mark all as touched so every error is shown
    const newErrors = validateAll(formData);
    setErrors(newErrors);
    setTouched(FIELD_ORDER.reduce((acc, k) => ({ ...acc, [k]: true }), {}));

    const firstInvalid = FIELD_ORDER.find((f) => newErrors[f]);
    if (firstInvalid) {
      toast.error("Please fix the highlighted fields");
      const el = document.querySelector(`[name="${firstInvalid}"]`);
      if (el) el.focus();
      return;
    }

    const trimmedVillage = formData.village.trim();

    const payload = {
      beneficiary_name: formData.name.trim(),
      relation_name: formData.relation.trim() || undefined,
      epic_no: formData.voterId.trim() || undefined,
      age: formData.age ? Number(formData.age) : undefined,
      qualification: formData.qualification.trim(),
      aadhaar_number: formData.aadhaar.trim() || undefined,
      mobile_number: formData.mobile.trim() || undefined,
      bank_account_number: formData.bankAccount.trim() || undefined,
      ifsc_code: formData.ifsc.trim() || undefined,
      amount: formData.amount ? Number(formData.amount) : undefined,
      mandal_name: formData.mandal,
      village_name: trimmedVillage,
      application_date: formData.date || undefined,
      status: formData.status,
      photo_url: formData.photoUrl.trim() || undefined,
      document_url: formData.documentUrl.trim() || undefined,
      remarks: formData.remarks.trim() || undefined,
    };

    setSubmitting(true);
    try {
      console.log("Yuvagalam create payload sent:", payload);
      await yuvagalamAPI.create(payload);

      // Success toast — stays visible after navigating to the table page
      toast.success("Form submitted successfully", { toastId: "yuvagalam-form-success" });

      handleReset();
      navigate("/dashboard/YuvagalamTable");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="yuvagalam-form-container">
      <div className="yuvagalam-form-card">
        <div className="yuvagalam-form-header">
          <h2>Add New Yuvagalam Beneficiary</h2>
          <p>20 lakh jobs for youth over five years, with unemployment support</p>
          <button type="button" className="yuvagalam-back-btn" onClick={() => window.history.back()}>
            Back
          </button>
        </div>

        {/* noValidate -> disables browser default popups so our custom messages show */}
        <form onSubmit={handleSubmit} noValidate>
          <div className="yuvagalam-form-grid">
            <div className="yuvagalam-form-group">
              <label>Beneficiary Name *</label>
              <input
                type="text"
                name="name"
                placeholder="Enter Beneficiary Name"
                value={formData.name}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("name")}
                maxLength={100}
              />
              {showError("name") && <span className="error-text">{errors.name}</span>}
            </div>

            <div className="yuvagalam-form-group">
              <label>Relation (Ex: S/O, W/O, D/O)*</label>
              <input
                type="text"
                name="relation"
                placeholder="Ex: S/O, D/O"
                value={formData.relation}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("relation")}
                maxLength={100}
              />
              {showError("relation") && <span className="error-text">{errors.relation}</span>}
            </div>

            <div className="yuvagalam-form-group">
              <label>Voter Id *</label>
              <input
                type="text"
                name="voterId"
                placeholder="Enter voter Id (e.g. ABC1234567)"
                value={formData.voterId}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("voterId")}
                maxLength={10}
              />
              {showError("voterId") && <span className="error-text">{errors.voterId}</span>}
            </div>

            <div className="yuvagalam-form-group">
              <label>Age *</label>
              <input
                type="text"
                inputMode="numeric"
                name="age"
                placeholder={`Enter Age (${MIN_AGE}–${MAX_AGE})`}
                value={formData.age}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("age")}
                maxLength={2}
              />
              {showError("age") && <span className="error-text">{errors.age}</span>}
            </div>

            <div className="yuvagalam-form-group">
              <label>Qualification *</label>
              <input
                type="text"
                name="qualification"
                placeholder="Ex: B.Tech, Degree, ITI"
                value={formData.qualification}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("qualification")}
                maxLength={100}
              />
              {showError("qualification") && <span className="error-text">{errors.qualification}</span>}
            </div>

            <div className="yuvagalam-form-group">
              <label>Aadhaar Number *</label>
              <input
                type="text"
                inputMode="numeric"
                name="aadhaar"
                placeholder="Enter 12-digit Aadhaar Number"
                value={formData.aadhaar}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("aadhaar")}
                maxLength={12}
              />
              {showError("aadhaar") && <span className="error-text">{errors.aadhaar}</span>}
            </div>

            <div className="yuvagalam-form-group">
              <label>Mobile Number *</label>
              <input
                type="tel"
                inputMode="numeric"
                name="mobile"
                placeholder="Enter 10-digit Mobile Number"
                value={formData.mobile}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("mobile")}
                maxLength={10}
              />
              {showError("mobile") && <span className="error-text">{errors.mobile}</span>}
            </div>

            <div className="yuvagalam-form-group">
              <label>Bank Account Number *</label>
              <input
                type="text"
                inputMode="numeric"
                name="bankAccount"
                placeholder="Enter Bank Account Number"
                value={formData.bankAccount}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("bankAccount")}
                maxLength={18}
              />
              {showError("bankAccount") && <span className="error-text">{errors.bankAccount}</span>}
            </div>

            <div className="yuvagalam-form-group">
              <label>IFSC Code *</label>
              <input
                type="text"
                name="ifsc"
                placeholder="Enter IFSC Code (e.g. SBIN0001234)"
                value={formData.ifsc}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("ifsc")}
                maxLength={11}
              />
              {showError("ifsc") && <span className="error-text">{errors.ifsc}</span>}
            </div>

            <div className="yuvagalam-form-group">
              <label>Monthly Allowance (₹) *</label>
              {/* type="text" + inputMode="decimal" so we can show our own error for non-numeric input */}
              <input
                type="text"
                inputMode="decimal"
                name="amount"
                placeholder="Enter Amount (numbers only)"
                value={formData.amount}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("amount")}
                maxLength={10}
              />
              {showError("amount") && <span className="error-text">{errors.amount}</span>}
            </div>

            

            <div className="yuvagalam-form-group">
              <label>Mandal *</label>
              <select
                name="mandal"
                value={formData.mandal}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("mandal")}
              >
                <option value="">Select Mandal</option>
                {MANDAL_NAMES.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
              {showError("mandal") && <span className="error-text">{errors.mandal}</span>}
            </div>

              <div className="yuvagalam-form-group">
              <label>Village *</label>
              <input
                type="text"
                name="village"
                placeholder="Enter Village (alphabets only)"
                value={formData.village}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("village")}
                maxLength={100}
              />
              {showError("village") && <span className="error-text">{errors.village}</span>}
              <small style={{ color: "#9ca3af", fontSize: "12px", marginTop: "4px", display: "block" }}>
                Must exactly match an existing village name under the selected Mandal.
              </small>
            </div>
            <div className="yuvagalam-form-group">
              <label>Date *</label>
              <input
                type="date"
                name="date"
                value={formData.date}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("date")}
                min="1990-01-01"
                max="2100-12-31"
              />
              {showError("date") && <span className="error-text">{errors.date}</span>}
            </div>

            <div className="yuvagalam-form-group">
              <label>Status *</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("status")}
              >
                <option value="">Select Status</option>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                ))}
              </select>
              {showError("status") && <span className="error-text">{errors.status}</span>}
            </div>

            <div className="yuvagalam-form-group">
              <label>Photo URL (Optional)</label>
              <input
                type="url"
                name="photoUrl"
                placeholder="https://..."
                value={formData.photoUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("photoUrl")}
              />
              {showError("photoUrl") && <span className="error-text">{errors.photoUrl}</span>}
            </div>

            <div className="yuvagalam-form-group">
              <label>Supporting Document URL (Optional)</label>
              <input
                type="url"
                name="documentUrl"
                placeholder="https://..."
                value={formData.documentUrl}
                onChange={handleChange}
                onBlur={handleBlur}
                className={inputClass("documentUrl")}
              />
              {showError("documentUrl") && <span className="error-text">{errors.documentUrl}</span>}
            </div>
          </div>

          <div className="yuvagalam-form-group yuvagalam-full-width">
            <label>Remarks (Optional)</label>
            <textarea
              rows="4"
              name="remarks"
              placeholder="Enter Remarks"
              value={formData.remarks}
              onChange={handleChange}
              onBlur={handleBlur}
              className={inputClass("remarks")}
              maxLength={500}
            ></textarea>
            {showError("remarks") && <span className="error-text">{errors.remarks}</span>}
            <small className="char-count">{formData.remarks.length}/500</small>
          </div>

          <div className="yuvagalam-btn-group">
            <button type="submit" className="yuvagalam-submit-btn" disabled={submitting}>
              {submitting ? "Submitting..." : "Submit"}
            </button>
            <button type="button" className="yuvagalam-reset-btn" onClick={handleReset}>
              Reset
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default YuvagalamForm;